import { DBFilterValues } from 'apolloClient/types/Searches';
import { FiltersValues } from 'components/UnitsFilters/types';
import { omit } from 'lodash';
import { addSearchboxRelationsFilters } from 'components/Search/utils/searchUtils';

const composeAnyValue = ({
  fieldName,
  max,
  min,
}: {
  min?: number;
  max?: number;
  fieldName: keyof FiltersValues;
}): Partial<FiltersValues> | {} =>
  min || max
    ? {
        [fieldName]: {
          max: max === null ? 'Any' : String(max),
          min: min === null ? 'Any' : String(min),
        },
      }
    : {};

export const filterValuesFromDb = (
  values: DBFilterValues
): Partial<FiltersValues> => {
  const {
    addresses,
    buildings,
    cities,
    counties,
    neighborhoods,
    zipCodes,
    minBeds,
    maxBeds,
    minPrice,
    maxPrice,
    baths,
    minFloor,
    maxFloor,
    maxHOA,
    parkingPlaces,
    minSqft,
    maxSqft,
    minPriceSqFt,
    maxPriceSqFt,
    minUnitsInBuilding,
    maxUnitsInBuilding,
    minYearBuild,
    maxYearBuild,
    daysOnCbb,
    shortTermRentTerms,
  } = values;

  const otherValues = omit(values, [
    'addresses',
    'buildings',
    'cities',
    'counties',
    'neighborhoods',
    'zipCodes',
    'minBeds',
    'maxBeds',
    'minPrice',
    'maxPrice',
    'baths',
    'minFloor',
    'maxFloor',
    'maxHOA',
    'parkingPlaces',
    'minSqft',
    'maxSqft',
    'minPriceSqFt',
    'maxPriceSqFt',
    'minUnitsInBuilding',
    'maxUnitsInBuilding',
    'minYearBuild',
    'maxYearBuild',
    'daysOnCbb',
    'shortTermRentTerms',
    '__typename',
  ]) as Partial<FiltersValues>;

  Object.assign(
    otherValues,
    composeAnyValue({ min: minBeds, max: maxBeds, fieldName: 'anyBeds' })
  );
  Object.assign(
    otherValues,
    composeAnyValue({ min: minPrice, max: maxPrice, fieldName: 'anyPrice' })
  );
  Object.assign(
    otherValues,
    composeAnyValue({ min: minFloor, max: maxFloor, fieldName: 'floor' })
  );
  Object.assign(
    otherValues,
    composeAnyValue({
      min: minPriceSqFt,
      max: maxPriceSqFt,
      fieldName: 'priceSqFt',
    })
  );
  Object.assign(
    otherValues,
    composeAnyValue({ min: minSqft, max: maxSqft, fieldName: 'sqft' })
  );
  Object.assign(
    otherValues,
    composeAnyValue({
      min: minUnitsInBuilding,
      max: maxUnitsInBuilding,
      fieldName: 'unitsInBuilding',
    })
  );
  Object.assign(
    otherValues,
    composeAnyValue({
      min: minYearBuild,
      max: maxYearBuild,
      fieldName: 'yearBuild',
    })
  );
  Object.assign(otherValues, { baths: baths ? String(baths) : undefined });
  Object.assign(otherValues, { maxHOA: maxHOA ? String(maxHOA) : undefined });
  Object.assign(otherValues, {
    parkingPlaces: parkingPlaces ? String(parkingPlaces) : undefined,
  });
  Object.assign(otherValues, { baths: baths ? String(baths) : undefined });
  Object.assign(otherValues, {
    daysOnCbb: daysOnCbb ? String(daysOnCbb) : undefined,
  });

  const searchBoxFilters = addSearchboxRelationsFilters(
    {
      addresses,
      buildings,
      cities,
      counties,
      neighborhoods,
    },
    {
      cities: [],
      counties: [],
      zipCodes: [],
      neighborhoods: [],
      buildings: [],
      addresses: [],
    }
  );

  Object.assign(searchBoxFilters, {
    zipCodes: zipCodes?.map(({ code }) => code),
  });

  Object.assign(searchBoxFilters, {
    shortTermRentTerms: shortTermRentTerms?.map(({ term }) => term),
  });

  Object.assign(searchBoxFilters, {
    cities: searchBoxFilters?.cities?.map((city) => city?.slug),
  });

  Object.assign(searchBoxFilters, {
    counties: searchBoxFilters?.counties?.map((county) => county?.slug),
  });

  Object.assign(searchBoxFilters, {
    neighborhoods: searchBoxFilters?.neighborhoods?.map(
      (neighborhood) => neighborhood?.slug
    ),
  });

  Object.assign(searchBoxFilters, {
    buildings: searchBoxFilters?.buildings?.map((building) => building?.slug),
  });

  Object.assign(searchBoxFilters, {
    addresses: searchBoxFilters?.addresses?.map((address) => address?.name),
  });

  return Object.assign(otherValues, searchBoxFilters);
};
